<script setup>
import {useI18n} from 'vue-i18n'
import { isEmpty } from 'lodash';
const { t } = useI18n()
</script>
<script>
  export default {
    name: "DpCopyToClipboard",
    props: {
      value: [String,Number],
      inline: {type: Boolean, default: false},
      label: {type: String, default: undefined},
      rtl: {type: Boolean, default: false},
      disableAnimation: {type: Boolean, default: false}
    },
    data() {
      return {
        popMessage: false,
        areaFlash: false
      };
    },
    computed: {
      copyString() {return this.value.toString()}
    },
    methods: {
      popMessageAnimation() {
        this.popMessage = true;
        let self = this;
        setTimeout(function () {
          self.popMessage = false;
        }, 2500);
      },
      areaFlashAnimation() {
        this.areaFlash = true;
        let self = this;
        setTimeout(function () {
          self.areaFlash = false;
        }, 250);
      },
      copyToClipboard() {
        // https://hackernoon.com/copying-text-to-clipboard-with-javascript-df4d4988697f (17/09/2020)
        const el = document.createElement('textarea');  // Create a <textarea> element
        el.value = this.copyString;                                 // Set its value to the string that you want copied
        el.setAttribute('readonly', '');                // Make it readonly to be tamper-proof
        el.style.position = 'absolute';
        el.style.left = '-9999px';                      // Move outside the screen to make it invisible
        document.body.appendChild(el);                  // Append the <textarea> element to the HTML document
        const selected =
          document.getSelection().rangeCount > 0        // Check if there is any content selected previously
            ? document.getSelection().getRangeAt(0)     // Store selection if found
            : false;                                    // Mark as false to know no selection existed before
        el.select();                                    // Select the <textarea> content
        document.execCommand('copy');                   // Copy - only works as a result of a user action (e.g. click events)
        document.body.removeChild(el);                  // Remove the <textarea> element
        if (selected) {                                 // If a selection existed before copying
          document.getSelection().removeAllRanges();    // Unselect everything on the HTML document
          document.getSelection().addRange(selected);   // Restore the original selection
        }
        if (!this.disableAnimation) {
          this.popMessageAnimation();
          this.areaFlashAnimation();
        }
      }
    }
  }
</script>

<template>
  <div class="area-flash" :class="{'flash-outline': areaFlash && !!($slots.default)}">
    <slot></slot>
    <div class="dp-flex row" :class="{'inline': inline}">

      <div class="pop-message" v-show="popMessage">
        <span class="pop-message__content">
          <i class="el-icon-success" :class="{'flip-icon': rtl}"/>{{t('copied.message')}}
        </span>
      </div>

      <dp-button tabindex="0" purpose="secondaryAction" size="small" style="margin-top: 0.5em;"
                  @click="copyToClipboard">
        <span>
          <i class="el-icon-copy-document" :class="{'flip-icon': rtl}"/>{{(isEmpty(label)) ? t('copy.label') : label}}
        </span>
      </dp-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .inline {
    display: inline-block;
  }

  i {
    margin: 0;
    margin-inline-end: .3em;
  }

  .pop-message__content {
    padding: 0.15rem 0.3rem;
    color: var(--use-theme__event__success__font-color);
    background-color: var(--use-theme__event__success__background-color);
    border-radius: 6px;
  }

  .pop-message {
    position: absolute;
    animation-duration: 2500ms;
    animation-delay: 380ms;
    transform: translateY(1rem);
    transition: all ease-in;
    opacity: 0;
    animation-name: flashHint;
  }

  @keyframes flashHint {
    from {
      transform: translateY(.5rem);
      opacity: 0;
    }
    45% {
      opacity: 1;
      transform: translateY(-1.1rem);
    }
    to {
      opacity: 0;
      transform: translateY(-1.5rem);
    }
  }

  .area-flash {
    transition: background-color 250ms linear;
    &.flash-outline {
      border-radius: 8px;
      background-color: var(--use-theme__event__success__background-color);
    }
  }

</style>

<i18n>
{
  "ar-AE": {
    "copy": {
      "label": "نسخ"
    },
    "copied": {
      "message": "تم النسخ إلى الحافظة"
    }
  },
  "ar-EG": {
    "copy": {
      "label": "نسخ"
    },
    "copied": {
      "message": "تم النسخ إلى الحافظة"
    }
  },
  "ar-SA": {
    "copy": {
      "label": "نسخ"
    },
    "copied": {
      "message": "تم النسخ إلى الحافظة"
    }
  },
  "el-GR": {
    "copy": {
      "label": "Αντιγραφή"
    },
    "copied": {
      "message": "Αντιγράφηκε στο πρόχειρο"
    }
  },
  "el-CY": {
    "copy": {
      "label": "Αντιγραφή"
    },
    "copied": {
      "message": "Αντιγράφηκε στο πρόχειρο"
    }
  },
  "de-DE": {
    "copy": {
      "label": "Kopieren"
    },
    "copied": {
      "message": "In die Zwischenablage kopiert"
    }
  },
  "de-AT": {
    "copy": {
      "label": "Kopieren"
    },
    "copied": {
      "message": "In die Zwischenablage kopiert"
    }
  },
  "de-CH": {
    "copy": {
      "label": "Kopieren"
    },
    "copied": {
      "message": "In die Zwischenablage kopiert"
    }
  },
  "en-US": {
    "copy": {
      "label": "Copy"
    },
    "copied": {
      "message": "Copied to clipboard"
    }
  },
  "en-GB": {
    "copy": {
      "label": "Copy"
    },
    "copied": {
      "message": "Copied to clipboard"
    }
  },
  "it-IT": {
    "copy": {
      "label": "Copia"
    },
    "copied": {
      "message": "Copiato negli appunti"
    }
  },
  "fr-FR": {
    "copy": {
      "label": "Copier"
    },
    "copied": {
      "message": "Copié dans le presse-papier"
    }
  },
  "es-ES": {
    "copy": {
      "label": "Copiar"
    },
    "copied": {
      "message": "Copiado al portapapeles"
    }
  },
  "pl-PL": {
    "copy": {
      "label": "Skopiuj"
    },
    "copied": {
      "message": "Skopiowane do schowka"
    }
  },
  "nl-NL": {
    "copy": {
      "label": "Kopiëren"
    },
    "copied": {
      "message": "Gekopieerd naar klembord"
    }
  },
  "bg-BG": {
    "copy": {
      "label": "Копиране"
    },
    "copied": {
      "message": "Копирано в клипборда"
    }
  },
  "bs-BA": {
    "copy": {
      "label": "Kopiraj"
    },
    "copied": {
      "message": "Kopirano u međuspremnik"
    }
  },
  "da-DK": {
    "copy": {
      "label": "Kopier"
    },
    "copied": {
      "message": "Kopieret til udklipsholderen"
    }
  },
  "et-EE": {
    "copy": {
      "label": "Kopeeri"
    },
    "copied": {
      "message": "Kopeeritud lõikelauale"
    }
  },
  "fi-FI": {
    "copy": {
      "label": "Kopioi"
    },
    "copied": {
      "message": "Kopioitu leikepöydälle"
    }
  },
  "he-IL": {
    "copy": {
      "label": "עותק"
    },
    "copied": {
      "message": "הועתק ללוח"
    }
  },
  "ga-IE": {
    "copy": {
      "label": "Cóipeáil"
    },
    "copied": {
      "message": "Cóipeáladh chuig an ngearrthaisce é"
    }
  },
  "ja-JP": {
    "copy": {
      "label": "コピー"
    },
    "copied": {
      "message": "クリップボードにコピー"
    }
  },
  "hr-HR": {
    "copy": {
      "label": "Kopiraj"
    },
    "copied": {
      "message": "Kopirano u međuspremnik"
    }
  },
  "lv-LV": {
    "copy": {
      "label": "Kopēt"
    },
    "copied": {
      "message": "Nokopēts starpliktuvē"
    }
  },
  "lt-LT": {
    "copy": {
      "label": "Kopijuoti"
    },
    "copied": {
      "message": "Nukopijuota į mainų sritį"
    }
  },
  "pt-PT": {
    "copy": {
      "label": "Copiar"
    },
    "copied": {
      "message": "Copiado para a área de transferência"
    }
  },
  "ro-RO": {
    "copy": {
      "label": "Copiere"
    },
    "copied": {
      "message": "Copiat în clipboard"
    }
  },
  "ru-RU": {
    "copy": {
      "label": "Скопировать"
    },
    "copied": {
      "message": "Скопировано в буфер обмена"
    }
  },
  "sv-SE": {
    "copy": {
      "label": "Kopiera"
    },
    "copied": {
      "message": "Kopieras till Urklipp"
    }
  },
  "sk-SK": {
    "copy": {
      "label": "Kópia"
    },
    "copied": {
      "message": "Skopírované do schránky"
    }
  },
  "cs-CZ": {
    "copy": {
      "label": "Kopírovat"
    },
    "copied": {
      "message": "Zkopírováno do schránky"
    }
  },
  "hu-HU": {
    "copy": {
      "label": "Másol"
    },
    "copied": {
      "message": "A vágólapra másolva"
    }
  },
  "fr-BE": {
    "copy": {
      "label": "Copier"
    },
    "copied": {
      "message": "Copié dans le presse-papier"
    }
  },
  "fr-LU": {
    "copy": {
      "label": "Copier"
    },
    "copied": {
      "message": "Copié dans le presse-papier"
    }
  },
  "mt-MT": {
    "copy": {
      "label": "Ikkopja"
    },
    "copied": {
      "message": "Ikkoppjat fuq il-klibbord"
    }
  },
  "sl-SI": {
    "copy": {
      "label": "Kopiraj"
    },
    "copied": {
      "message": "Kopirano v odložišče"
    }
  },
  "tr-TR": {
    "copy": {
      "label": "Kopyala"
    },
    "copied": {
      "message": "Panoya kopyalandı"
    }
  },
  "tr-CY": {
    "copy": {
      "label": "Kopyala"
    },
    "copied": {
      "message": "Panoya kopyalandı"
    }
  },
  "uk-UA": {
    "copy": {
      "label": "Kopieren"
    },
    "copied": {
      "message": "In die Zwischenablage kopiert"
    }
  },
  "zh-CN": {
    "copy": {
      "label": "复制"
    },
    "copied": {
      "message": "复制到剪贴板"
    }
  }
}
</i18n>
